/**
 * Vertragsbaukasten Schritt 4: Galerie, Klausel-Bibliothek und Sprachpaar (Daten und reine Logik).
 * Bauplan docs/vertragsbaukasten/bericht-vertraege.md 3.3 (Galerie, Klausel-Bibliothek) und 3.5; Migrationen 0136, 0137.
 */
import { walkBlocks, validateBlocks, type Block, type ContractRole, type CustomVariableDef, type FieldRef, type Inline, type Language } from "@talentkontor/domain";
import type { Api } from "../../../../data/api";
import { t, nameIn, istCreatorKonto } from "../../../../lib/sprache";
import { alleIds, entferne, genutzteFelder, neueId } from "./modell";

// ---------------------------------------------------------------------------
// Galerie
// ---------------------------------------------------------------------------

export type Geschaeftsmodell = "broker" | "reseller" | "both";

export interface GalerieZeile {
  key: string;
  kind_key: string;
  kind_name_de: string;
  kind_name_en: string;
  language: Language;
  name: string;
  version: number;
  description: string | null;
  required_signers: ContractRole[];
  business_model: Geschaeftsmodell;
  body_html: string;
  annex_html: string | null;
  annex_hidden_from: ContractRole[];
  variables: CustomVariableDef[];
  blocks: Block[] | null;
  /** 0181: Rechtsordnung und Stand des Musters */
  jurisdiction?: string | null;
  legal_as_of?: string | null;
  /** 0201: Vorlage aus einem gekauften Vorlagenpaket (sonst null) */
  package?: string | null;
}

/** Muster der eigenen Kontoart (0197): Agentur „agency“ und „both“, Creator-Konto „creator“ und „both“ */
export const musterFuerKonto = (): [string, "in", string[]] => ["audience", "in", ["both", istCreatorKonto() ? "creator" : "agency"]];

export const loadGalerie = (api: Api) => api.select<GalerieZeile>("platform_contract_templates", {
  filters: [musterFuerKonto()],
  columns: "key, kind_key, kind_name_de, kind_name_en, language, name, version, description, required_signers, business_model, body_html, annex_html, annex_hidden_from, variables, blocks, jurisdiction, legal_as_of, package",
  order: [["key", "asc"], ["language", "asc"]],
});

// ---------------------------------------------------------------------------
// Vorlagenpakete (0201): Katalog und Käufe des Kontos. Die Texte eines Pakets liefert loadGalerie erst nach dem Kauf.
// ---------------------------------------------------------------------------

export interface Vorlagenpaket {
  key: string;
  name_de: string;
  name_en: string;
  description_de: string;
  description_en: string;
  contents: { de: string; en: string }[];
  price_cents: number;
  currency: string;
  audience: "agency" | "creator" | "both";
}
export interface GekauftesPaket { package: string; purchased_at: string }

export const loadPakete = (api: Api) => api.select<Vorlagenpaket>("template_packages", {
  filters: [["active", "eq", true], musterFuerKonto()],
  columns: "key, name_de, name_en, description_de, description_en, contents, price_cents, currency, audience",
  order: [["sort_order", "asc"], ["key", "asc"]],
});
export const loadGekauftePakete = (api: Api, orgId: string) => api.select<GekauftesPaket>("org_template_packages", {
  filters: [["org_id", "eq", orgId]],
  columns: "package, purchased_at",
});

export interface GalerieKarte {
  key: string;
  name: string;
  beschreibung: string;
  kuerzel: string;
  modell: Geschaeftsmodell;
  parteien: ContractRole[];
  merkmale: string[];
  sprachen: Language[];
  zeilen: Partial<Record<Language, GalerieZeile>>;
}

/** Name ohne „(Muster)“/„(sample)“ bzw. „(Paket)“/„(pack)“ */
const ohneMuster = (n: string) => n.replace(/\s*\((Muster|sample|Paket|pack)\)\s*$/i, "");

/** Karten je Vorlage (beide Sprachen zusammen), in der Sprache der Oberfläche beschriftet. */
export function galerieKarten(zeilen: readonly GalerieZeile[], lang: Language): GalerieKarte[] {
  const byKey = new Map<string, GalerieZeile[]>();
  for (const z of zeilen) byKey.set(z.key, [...(byKey.get(z.key) ?? []), z]);
  return [...byKey.entries()].map(([key, rows]) => {
    const z = rows.find((r) => r.language === lang) ?? rows[0]!;
    const text = rows.map((r) => `${r.body_html}${r.annex_html ?? ""}`).join(" ");
    const merkmale: string[] = [];
    if (z.annex_hidden_from.length) merkmale.push(t("vertraulicher Teil", "confidential part"));
    if (text.includes("talent.is_minor")) merkmale.push(t("für Minderjährige vorbereitet", "prepared for minors"));
    if (text.includes("<table")) merkmale.push(t("Tabelle", "table"));
    const wahl = (z.blocks ?? []).length ? [...walkBlocks(z.blocks!)].filter((x) => x.block.type === "choice").length : (z.variables ?? []).filter((v) => (v as { ui?: string }).ui === "choice").length;
    if (wahl) merkmale.push(t(`${wahl} wählbare Klausel${wahl > 1 ? "n" : ""}`, `${wahl} optional clause${wahl > 1 ? "s" : ""}`));
    if (z.blocks) merkmale.push(t("in Bausteinen", "in blocks"));
    if (z.package) merkmale.unshift(t("Vorlagenpaket", "template pack"));
    const name = ohneMuster(z.name);
    return {
      key, name, beschreibung: z.description ?? nameIn(z.kind_name_de, z.kind_name_en), kuerzel: kuerzel(name, key), modell: z.business_model,
      parteien: z.required_signers, merkmale, sprachen: rows.map((r) => r.language).sort() as Language[],
      zeilen: Object.fromEntries(rows.map((r) => [r.language, r])) as Partial<Record<Language, GalerieZeile>>,
    };
  }).sort((a, b) => a.name.localeCompare(b.name, lang));
}

/** Feste Kürzel der CreatorOffice-Vorlagen (nie „DE“/„EN“, damit sie nicht wie eine Sprache aussehen) */
const KUERZEL: Record<string, string> = {
  cooperation: "KV", framework: "MV", brand_framework: "RM", confidentiality: "NDA", confidentiality_creator: "NDA", ugc: "UGC", barter: "BG",
  usage_extension: "NT", brand_order: "AUF", creator_order: "EA", usage_rights: "NR", guardian_consent: "EW",
};

function kuerzel(name: string, key?: string): string {
  if (key && KUERZEL[key]) return KUERZEL[key]!;
  const w = name.split(/[\s-]+/).filter((x) => /^\p{L}/u.test(x) && !/^(und|and|an|mit|with|der|des|the|of|to|von)$/i.test(x));
  const k = (w.length >= 2 ? w.slice(0, 3).map((x) => x[0]) : [...(w[0] ?? "V").slice(0, 2)]).join("").toUpperCase();
  return k === "DE" || k === "EN" ? `${k}V` : k;
}

/** Passt eine Karte zur gewählten Arbeitsweise? „both“ passt immer. */
export function passtZu(k: Pick<GalerieKarte, "modell">, arbeitsweise: "broker" | "reseller" | null): boolean {
  return !arbeitsweise || k.modell === "both" || k.modell === arbeitsweise;
}

// ---------------------------------------------------------------------------
// Klausel-Bibliothek
// ---------------------------------------------------------------------------

export const KATEGORIEN = ["services", "fee", "usage_rights", "exclusivity", "disclosure", "minors", "privacy", "term", "final", "other"] as const;
export type Kategorie = (typeof KATEGORIEN)[number];
export function kategorieLabel(k: string): string {
  switch (k) {
    case "services": return t("Leistungen", "Services");
    case "fee": return t("Vergütung", "Fee");
    case "usage_rights": return t("Nutzungsrechte", "Usage rights");
    case "exclusivity": return t("Exklusivität", "Exclusivity");
    case "disclosure": return t("Kennzeichnung", "Labelling");
    case "minors": return t("Minderjährige", "Minors");
    case "privacy": return t("Datenschutz", "Data protection");
    case "term": return t("Laufzeit", "Term");
    case "final": return t("Schlussbestimmungen", "Final provisions");
    default: return t("Sonstiges", "Other");
  }
}

export interface KlauselZeile {
  /** Kennung im Baustein „clause“: eigene Klausel = Schlüssel, CreatorOffice-Klausel = „tk-“ + Schlüssel */
  clauseId: string;
  id: string | null;
  key: string;
  language: Language;
  title: string;
  category: string;
  blocks: Block[];
  /** eigene Angaben, die die Klausel nutzt (0136) */
  variables: CustomVariableDef[];
  version: number;
  eigen: boolean;
  archived_at: string | null;
  updated_at: string | null;
}

export async function loadKlauseln(api: Api, orgId: string): Promise<KlauselZeile[]> {
  const [eigene, plattform] = await Promise.all([
    api.select<{ id: string; key: string; language: Language; title: string; category: string; blocks: Block[]; variables: CustomVariableDef[]; version: number; archived_at: string | null; updated_at: string }>("contract_clauses", {
      columns: "id, key, language, title, category, blocks, variables, version, archived_at, updated_at", filters: [["org_id", "eq", orgId]], order: [["title", "asc"]],
    }),
    api.select<{ key: string; language: Language; title: string; category: string; blocks: Block[]; variables: CustomVariableDef[]; version: number }>("platform_contract_clauses", {
      columns: "key, language, title, category, blocks, variables, version", order: [["sort_order", "asc"], ["key", "asc"]],
    }),
  ]);
  return [
    ...eigene.map((k) => ({ ...k, clauseId: k.key, eigen: true })),
    ...plattform.map((k) => ({ ...k, id: null, clauseId: `tk-${k.key}`, eigen: false, archived_at: null, updated_at: null })),
  ];
}

/** Aktuelle Klausel zu einer Kennung und Sprache (für „neuere Fassung verfügbar“); archivierte zählen nicht. */
export function klauselZu(liste: readonly KlauselZeile[], clauseId: string, lang: Language): KlauselZeile | undefined {
  return liste.find((k) => k.clauseId === clauseId && k.language === lang && !k.archived_at);
}

/** Feld- und Bedingungsverweise auf eigene Angaben umbenennen (alt → neu), in Bausteinen und Text. */
function umbenennen(blocks: readonly Block[], map: ReadonlyMap<string, string>): Block[] {
  if (!map.size) return [...blocks];
  const ref = (f: FieldRef): FieldRef => (f.src === "custom" && map.has(f.path) ? { src: "custom", path: map.get(f.path)! } : f);
  const inl = (ns: readonly Inline[]): Inline[] => ns.map((n) => (n.t === "field" ? { ...n, field: ref(n.field) } : n.t === "if" ? { ...n, when: ref(n.when), then: inl(n.then), ...(n.else ? { else: inl(n.else) } : {}) } : n));
  const b = (x: Block): Block => {
    switch (x.type) {
      case "heading": case "paragraph": case "note": return { ...x, text: inl(x.text) };
      case "list": return { ...x, items: x.items.map((it) => ({ ...it, text: inl(it.text), ...(it.when ? { when: ref(it.when) } : {}) })) };
      case "table": return { ...x, ...(x.header ? { header: x.header.map(inl) } : {}), rows: x.rows.map((r) => r.map(inl)) };
      case "field": return { ...x, field: ref(x.field) };
      case "condition": return { ...x, when: ref(x.when), children: x.children.map(b), ...(x.otherwise ? { otherwise: x.otherwise.map(b) } : {}) };
      case "choice": case "private": case "attachment": case "clause": return { ...x, children: x.children.map(b) };
      default: return x;
    }
  };
  return blocks.map(b);
}

/**
 * Klausel einfügen samt ihrer eigenen Angaben (Prüfung Schritt 4, Befund 1): fehlende Angaben kommen in die Vorlage; gibt es
 * den Schlüssel schon mit anderer Art, wird die Angabe der Klausel umbenannt.
 */
export function klauselEinfuegen(k: Pick<KlauselZeile, "clauseId" | "version" | "blocks" | "variables">, dokument: readonly Block[], defs: readonly CustomVariableDef[]): { block: Block; defs: CustomVariableDef[] } {
  const neu = [...defs];
  const map = new Map<string, string>();
  const vergeben = new Set([...defs.map((d) => d.key), ...[...walkBlocks(dokument)].flatMap(({ block }) => (block.type === "choice" ? [block.key] : []))]);
  for (const v of k.variables ?? []) {
    const da = neu.find((d) => d.key === v.key);
    if (da && da.type === v.type) continue;
    let key = v.key;
    if (da || vergeben.has(key)) {
      key = Array.from({ length: 50 }, (_, n) => `${v.key}_${n + 2}`).find((x) => !vergeben.has(x) && !neu.some((d) => d.key === x)) ?? `${v.key}_neu`;
      map.set(v.key, key);
    }
    vergeben.add(key);
    neu.push({ ...v, key });
  }
  return { block: klauselBaustein({ ...k, blocks: umbenennen(k.blocks, map) }, dokument, neu), defs: neu };
}

/**
 * „Aktualisieren“ (Befund 9): neue Fassung der Klausel an derselben Stelle. Kennungen und Schlüssel der Wahlklauseln bleiben,
 * wo die Bausteine der Reihe nach gleich sind (Sprachpaar und Werte bleiben verknüpft).
 */
export function klauselAktualisieren(alt: Extract<Block, { type: "clause" }>, k: Pick<KlauselZeile, "clauseId" | "version" | "blocks" | "variables">, dokument: readonly Block[], defs: readonly CustomVariableDef[]): { block: Block; defs: CustomVariableDef[] } {
  const ohne = entferne(dokument, alt.id);
  const r = klauselEinfuegen(k, ohne, defs);
  const alteBausteine = [...walkBlocks(alt.children)].map((x) => x.block);
  const neueBausteine = [...walkBlocks((r.block as Extract<Block, { type: "clause" }>).children)].map((x) => x.block);
  const gleicheForm = alteBausteine.length === neueBausteine.length && alteBausteine.every((b, i) => b.type === neueBausteine[i]!.type);
  const idMap = new Map<string, string>();
  const keyMap = new Map<string, string>();
  if (gleicheForm) neueBausteine.forEach((b, i) => {
    const a = alteBausteine[i]!;
    idMap.set(b.id, a.id);
    if (b.type === "choice" && a.type === "choice") keyMap.set(b.key, a.key);
    if (b.type === "list" && a.type === "list" && b.items.length === a.items.length) b.items.forEach((it, j) => idMap.set(it.id, a.items[j]!.id));
  });
  const um = (x: Block): Block => {
    const id = idMap.get(x.id) ?? x.id;
    switch (x.type) {
      case "list": return { ...x, id, items: x.items.map((it) => ({ ...it, id: idMap.get(it.id) ?? it.id })) };
      case "condition": return { ...x, id, children: x.children.map(um), ...(x.otherwise ? { otherwise: x.otherwise.map(um) } : {}) };
      case "choice": return { ...x, id, key: keyMap.get(x.key) ?? x.key, children: x.children.map(um) };
      case "private": case "attachment": case "clause": return { ...x, id, children: x.children.map(um) };
      default: return { ...x, id };
    }
  };
  const c = r.block as Extract<Block, { type: "clause" }>;
  return { block: { ...c, id: alt.id, children: c.children.map(um) }, defs: r.defs };
}

/** Eigene Angaben, die ein Baustein nutzt (für „Als Klausel speichern“); Wahlklauseln definieren sich selbst. */
export function genutzteAngaben(blocks: readonly Block[], defs: readonly CustomVariableDef[]): CustomVariableDef[] {
  const pfade = new Set(genutzteFelder(blocks).filter((f) => f.src === "custom").map((f) => f.path));
  for (const { block } of walkBlocks(blocks)) {
    if (block.type === "condition" && block.when.src === "custom") pfade.add(block.when.path);
    if (block.type === "list") for (const it of block.items) if (it.when?.src === "custom") pfade.add(it.when.path);
  }
  const wahl = new Set([...walkBlocks(blocks)].flatMap(({ block }) => (block.type === "choice" ? [block.key] : [])));
  return defs.filter((d) => pfade.has(d.key) && !wahl.has(d.key));
}

/**
 * Baustein „Klausel aus der Bibliothek“: Schnappschuss mit neuen Kennungen, damit dieselbe Klausel mehrfach und neben
 * anderen Bausteinen stehen kann; Wahlklauseln darin bekommen freie Schlüssel.
 */
export function klauselBaustein(k: Pick<KlauselZeile, "clauseId" | "version" | "blocks">, dokument: readonly Block[], defs: readonly CustomVariableDef[] = []): Block {
  const ids = alleIds(dokument, defs);
  const id = neueId(ids, "k"); ids.add(id);
  return { id, type: "clause", clauseId: k.clauseId, version: k.version, children: k.blocks.map((b) => neuKennung(b, ids)) };
}

function neuKennung(b: Block, ids: Set<string>): Block {
  const id = neueId(ids, "k"); ids.add(id);
  const kids = (l: Block[]) => l.map((x) => neuKennung(x, ids));
  switch (b.type) {
    case "list": return { ...b, id, items: b.items.map((it) => { const i = neueId(ids, "k"); ids.add(i); return { ...it, id: i }; }) };
    case "condition": return { ...b, id, children: kids(b.children), ...(b.otherwise ? { otherwise: kids(b.otherwise) } : {}) };
    case "choice": {
      let key = b.key;
      for (let n = 2; ids.has(`key:${key}`); n++) key = `${b.key}_${n}`;
      ids.add(`key:${key}`);
      return { ...b, id, key, children: kids(b.children) };
    }
    case "private": case "attachment": case "clause": return { ...b, id, children: kids(b.children) };
    default: return { ...b, id };
  }
}

/** Bausteine, die als Klausel gespeichert werden dürfen (wie app.contract_clause_blocks_ok, 0136). */
export function klauselFaehig(blocks: readonly Block[]): boolean {
  if (!blocks.length || blocks.length > 200 || validateBlocks(blocks) !== null) return false;
  return ![...walkBlocks(blocks)].some(({ block }) => block.type === "private" || block.type === "signatures" || block.type === "clause" || block.type === "legacy_html");
}

/** Klausel aus einem Baustein des Editors: eine Klausel aus der Bibliothek gibt ihre Bausteine her, sonst der Baustein selbst. */
export function klauselInhalt(b: Block): Block[] {
  return b.type === "clause" ? b.children : [b];
}

/** Eigene Klausel speichern: gleicher Schlüssel und Sprache → neue Version, sonst neu. */
export async function speichereKlausel(api: Api, orgId: string, k: { key: string; language: Language; title: string; category: string; blocks: Block[]; variables: CustomVariableDef[] }): Promise<void> {
  const vorhanden = await api.select<{ id: string }>("contract_clauses", { columns: "id", filters: [["org_id", "eq", orgId], ["key", "eq", k.key], ["language", "eq", k.language]] });
  if (vorhanden[0]) await api.update("contract_clauses", [["id", "eq", vorhanden[0].id]], { title: k.title, category: k.category, blocks: k.blocks, variables: k.variables, archived_at: null }, { columns: "id" });
  else await api.insert("contract_clauses", { org_id: orgId, ...k }, { columns: "id" });
}

// ---------------------------------------------------------------------------
// Sprachpaar
// ---------------------------------------------------------------------------

/** FNV-1a 64 bit als Hex (16 Zeichen): Prüfwert eines Bausteins ohne Leerraum-Angaben und ohne Kinder. */
export function pruefwert(b: Block): string {
  const eigen = JSON.stringify(b, (k, v) => (k === "ws" || k === "wsEnd" || k === "wsElse" || k === "sep" || k === "children" || k === "otherwise" ? undefined : v));
  let h = 0xcbf29ce484222325n;
  for (const c of new TextEncoder().encode(eigen)) { h ^= BigInt(c); h = (h * 0x100000001b3n) & 0xffffffffffffffffn; }
  return h.toString(16).padStart(16, "0");
}

/** Prüfwerte aller Bausteine (Kennung → Wert). */
export function pruefwerte(blocks: readonly Block[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const { block } of walkBlocks(blocks)) out[block.id] = pruefwert(block);
  return out;
}

/**
 * Welche Bausteine der anderen Sprache haben sich seit dem letzten Abgleich geändert? `quelle` sind die Bausteine der anderen
 * Sprache, `stand` die beim Abgleich gemerkten Prüfwerte (translation_source dieser Vorlage). Ohne Abgleich: null.
 */
export function uebersetzungPruefen(quelle: readonly Block[], stand: Record<string, string> | null | undefined): { geaendert: string[]; neu: string[] } | null {
  if (!stand) return null;
  const jetzt = pruefwerte(quelle);
  const geaendert = Object.keys(jetzt).filter((id) => id in stand && stand[id] !== jetzt[id]);
  const neu = Object.keys(jetzt).filter((id) => !(id in stand));
  return { geaendert, neu };
}

/** Die andere Sprache */
export const andereSprache = (l: Language): Language => (l === "de" ? "en" : "de");
